import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarHeart, Video, ShieldCheck, Clock, Mail } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { CALCOM, EMAIL, EMAIL_MAILTO } from "@/data/content";
import { useLang } from "@/i18n/LanguageContext";

const CHIP_ICONS = [Clock, ShieldCheck, Video];

export default function Booking() {
  const { t } = useLang();
  const b = t.booking;
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("type") === "coaching" ? "session" : "discovery");
  const initializedRef = useRef(new Set());

  // When focusing a newly revealed field inside the scheduling iframe, the browser
  // yanks the page instantly. Convert that abrupt jump into a smooth glide.
  useEffect(() => {
    let userInputUntil = 0;
    let programmaticUntil = 0;
    let lastY = window.scrollY;
    const markUserInput = () => {
      userInputUntil = Date.now() + 400;
    };
    const onScroll = () => {
      const now = Date.now();
      const y = window.scrollY;
      const abrupt = Math.abs(y - lastY) > 150;
      const iframeActive = document.activeElement && document.activeElement.tagName === "IFRAME";
      if (abrupt && iframeActive && now > userInputUntil && now > programmaticUntil) {
        const target = y;
        programmaticUntil = now + 150; // just long enough to swallow our own revert echo
        const distance = Math.abs(y - lastY);
        const glideDuration = Math.min(1.4, 0.4 + distance / 1200); // longer jumps get a longer, gentler glide
        if (window.__lenis) {
          window.__lenis.scrollTo(lastY, { immediate: true, force: true });
          window.__lenis.scrollTo(target, { duration: glideDuration });
        } else {
          window.scrollTo({ top: target, behavior: "smooth" });
        }
        lastY = target;
        return;
      }
      lastY = y;
    };
    window.addEventListener("wheel", markUserInput, { passive: true });
    window.addEventListener("touchstart", markUserInput, { passive: true });
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("wheel", markUserInput);
      window.removeEventListener("touchstart", markUserInput);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  const TABS = [
    { id: "discovery", label: b.tabs.discovery.label, hint: b.tabs.discovery.hint, calLink: CALCOM.discovery, namespace: "discoveryCall" },
    { id: "session", label: b.tabs.session.label, hint: b.tabs.session.hint, calLink: CALCOM.session, namespace: "coachingSession" },
  ];

  // Each event type gets its own cal.com namespace, initialized lazily the first
  // time its tab is shown (hidden containers measure zero width), then toggled.
  useEffect(() => {
    const active = TABS.find((x) => x.id === tab);
    if (initializedRef.current.has(active.id)) return;
    const el = document.getElementById(`cal-embed-${active.id}`);
    if (!el || !window.Cal) return;
    initializedRef.current.add(active.id);
    window.Cal("init", active.namespace, { origin: "https://cal.com" });
    window.Cal.ns[active.namespace]("ui", {
      theme: "light",
      styles: { branding: { brandColor: "#C86D51" } },
      cssVarsPerTheme: { light: { "cal-brand": "#C86D51", "cal-brand-emphasis": "#B25A40" } },
    });
    window.Cal.ns[active.namespace]("inline", {
      elementOrSelector: el,
      calLink: active.calLink,
      config: { theme: "light", layout: "month_view", iframeAttrs: { title: `${active.label} booking calendar` } },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  return (
    <>
      <section data-testid="booking-header" className="pt-32 sm:pt-40 pb-10 max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
        <FadeUp className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.25em] font-semibold text-terracotta mb-4">{b.kicker}</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-balance">
            {b.title}
          </h1>
          <p className="mt-6 text-base sm:text-lg text-clay leading-relaxed">
            {b.intro}
            {tab === "discovery" ? ` ${b.introFree}` : ""}
          </p>
        </FadeUp>

        <FadeUp delay={0.1} className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
          {b.chips.map((text, i) => {
            if (tab === "session" && i < 2) return null;
            const Icon = CHIP_ICONS[i];
            return (
              <div key={text} className="flex items-center gap-3 rounded-2xl bg-cream border border-border px-4 py-3.5">
                <Icon className="w-4 h-4 text-sage-dark shrink-0" />
                <p className="text-sm font-medium text-espresso">{text}</p>
              </div>
            );
          })}
        </FadeUp>
      </section>

      <section data-testid="booking-calendar-section" className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 pb-20">
        <FadeUp delay={0.15}>
          <div data-testid="booking-tabs" className="flex flex-col sm:flex-row gap-3 mb-6">
            {TABS.map((tb) => (
              <button
                key={tb.id}
                data-testid={`booking-tab-${tb.id}`}
                onClick={() => setTab(tb.id)}
                className={`flex-1 rounded-2xl px-6 py-4 text-left transition-[background-color,border-color] duration-200 border ${
                  tab === tb.id
                    ? "bg-terracotta text-linen border-terracotta"
                    : "bg-white text-espresso border-border hover:bg-cream"
                }`}
              >
                <span className="flex items-center gap-2 font-serif text-lg font-semibold">
                  <CalendarHeart className="w-5 h-5" />
                  {tb.label}
                </span>
                <span className={`block text-xs mt-1 ${tab === tb.id ? "text-linen/80" : "text-taupe"}`}>{tb.hint}</span>
              </button>
            ))}
          </div>

          <div className="rounded-3xl bg-white border border-border shadow-[0_24px_60px_-30px_rgba(44,37,32,0.3)] overflow-hidden">
            {TABS.map((tb) => (
              <div
                key={tb.id}
                id={`cal-embed-${tb.id}`}
                data-testid={`calendar-embed-${tb.id}`}
                aria-hidden={tab !== tb.id}
                className={`w-full min-w-0 min-h-[680px] ${tab === tb.id ? "block" : "hidden"}`}
              />
            ))}
          </div>

          <p className="mt-6 text-center text-sm text-taupe">
            {b.fallbackPre}{" "}
            <a href={EMAIL_MAILTO} data-testid="booking-email-fallback" className="font-semibold text-terracotta hover:text-terracotta-dark">
              <Mail className="inline w-4 h-4 mr-1 -mt-0.5" />
              {b.fallbackLink} {EMAIL}
            </a>{" "}
            {b.fallbackPost}
          </p>
        </FadeUp>
      </section>
    </>
  );
}
